/**
 * Number Lab: multiplication, division and divisibility you can shove around
 * with a finger. N blocks try to form a rectangle of R rows. If they all fit,
 * R is a factor and the array reads as R x C = N. If some are left over, that
 * leftover pile IS the remainder.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Plus, RotateCcw } from 'lucide-preact';
import type { Vec } from '../../engine/vec.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { divisibilityHint, divisorsUpTo } from '../../lib/numbers.ts';
import { Chip, CrayonRow, Stepper, ToyButton } from '../../ui/controls.tsx';

const MAX_BLOCKS = 100;
const MAX_ROWS = 12;
/** Space kept clear for the readout pill and the divisibility hint. */
const TOP_RESERVE = 116;
const BOTTOM_RESERVE = 84;

interface Block {
  pos: Vec;
  target: Vec;
  /** Bob phase so leftovers look restless rather than broken. */
  phase: number;
}

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

export function NumberLab({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const blocksRef = useRef<Block[]>([]);
  const sparksRef = useRef<Spark[]>([]);
  const clockRef = useRef(0);

  const [total, setTotal] = useState(12);
  const [rows, setRows] = useState(3);
  const [color, setColor] = useState(() => token('--crayon-teal'));

  const cols = Math.floor(total / rows);
  const leftover = total % rows;
  const fits = leftover === 0 && cols > 0;

  const live = useRef({ total, rows, color, fits });
  live.current = { total, rows, color, fits };

  // Celebrate the moment a number turns out to be a factor.
  const prevFits = useRef(fits);
  useEffect(() => {
    if (fits && !prevFits.current) {
      play('yay', 0.8);
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.4 }, partyColors(), 26, 420);
      }
    }
    prevFits.current = fits;
  }, [fits]);

  // Keep the block pool in step with the chosen total.
  useEffect(() => {
    const blocks = blocksRef.current;
    const canvas = canvasRef.current;
    const w = canvas?.getBoundingClientRect().width ?? 320;
    const h = canvas?.getBoundingClientRect().height ?? 480;
    while (blocks.length < total) {
      blocks.push({
        pos: { x: w * (0.2 + Math.random() * 0.6), y: h + 40 },
        target: { x: w / 2, y: h / 2 },
        phase: Math.random() * Math.PI * 2,
      });
    }
    if (blocks.length > total) blocks.length = total;
  }, [total]);

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const { rows: r, color: c } = live.current;
      const blocks = blocksRef.current;
      const n = blocks.length;
      const fullCols = Math.floor(n / r);
      const rest = n % r;

      // Fit the array between the readout pill (top) and the hint pill (bottom),
      // reserving a strip underneath for the leftovers when there are any.
      const padX = 22;
      const topArea = TOP_RESERVE;
      const leftoverRows = rest > 0 ? Math.ceil(rest / Math.min(rest, 6)) : 0;
      const usableH = height - topArea - BOTTOM_RESERVE;
      const arrayH = usableH / (1 + leftoverRows * 0.42);
      const pitch = Math.min((width - padX * 2) / Math.max(fullCols, 1), arrayH / r, 62);
      const cell = pitch * 0.86;
      const gap = pitch - cell;
      const gridW = Math.max(0, fullCols * pitch - gap);
      const gridH = r * pitch - gap;
      const originX = width / 2 - gridW / 2;
      const originY = topArea + Math.max(0, (usableH - gridH - leftoverRows * pitch * 0.95) / 2);

      // Assign targets: fill the rectangle column by column, then the leftovers.
      const spread = Math.max(1, Math.min(rest, 6));
      const leftoverTop = originY + gridH + pitch * 0.55;
      for (let i = 0; i < n; i++) {
        const b = blocks[i];
        if (i < fullCols * r) {
          const col = Math.floor(i / r);
          const row = i % r;
          b.target = { x: originX + col * pitch + cell / 2, y: originY + row * pitch + cell / 2 };
        } else {
          const k = i - fullCols * r;
          const lx = width / 2 - ((spread - 1) * pitch) / 2;
          b.target = {
            x: lx + (k % spread) * pitch,
            y: leftoverTop + Math.floor(k / spread) * pitch,
          };
        }
        const k = 1 - Math.exp(-dt * 11);
        b.pos.x += (b.target.x - b.pos.x) * k;
        b.pos.y += (b.target.y - b.pos.y) * k;
      }

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // Row guides make "3 rows" literal.
      if (fullCols > 0) {
        ctx.save();
        ctx.strokeStyle = token('--paper-line');
        ctx.lineWidth = 2;
        for (let row = 0; row < r; row++) {
          const y = originY + row * (cell + gap) + cell / 2;
          ctx.beginPath();
          ctx.moveTo(originX - 10, y);
          ctx.lineTo(originX + gridW + 10, y);
          ctx.stroke();
        }
        ctx.restore();
      }

      const inGrid = fullCols * r;
      const leftoverColor = token('--crayon-orange');
      for (let i = 0; i < n; i++) {
        const b = blocks[i];
        const isLeft = i >= inGrid;
        const bob = isLeft ? Math.sin(clockRef.current * 3 + b.phase) * 3 : 0;
        drawBlock(ctx, b.pos.x, b.pos.y + bob, cell, isLeft ? leftoverColor : c, isLeft);
      }

      drawSparks(ctx, sparksRef.current);
    },
    onDown: (p) => {
      // Tapping the paper adds one more block — counting by touch.
      if (blocksRef.current.length >= MAX_BLOCKS) return;
      blocksRef.current.push({
        pos: { ...p.pos },
        target: { ...p.pos },
        phase: Math.random() * Math.PI * 2,
      });
      play('count', Math.min(1, blocksRef.current.length / 40));
      setTotal(blocksRef.current.length);
    },
  });

  const divisors = divisorsUpTo(total, MAX_ROWS);
  const hint = divisibilityHint(total, rows);

  const reset = () => {
    blocksRef.current = [];
    setTotal(12);
    setRows(3);
    play('whoosh', 0.6);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Number lab" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label="One more block"
              hideLabel
              disabled={total >= MAX_BLOCKS}
              onClick={() => {
                play('count', Math.min(1, total / 40));
                setTotal((t) => Math.min(MAX_BLOCKS, t + 1));
              }}
            >
              <Plus size={24} strokeWidth={2.75} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel onClick={reset}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          {fits ? (
            <>
              <strong>
                {rows} x {cols} = {total}
              </strong>
              <span>no leftovers</span>
            </>
          ) : (
            <>
              <strong>
                {total} ÷ {rows} = {cols}
              </strong>
              <span>and {leftover} left over</span>
            </>
          )}
        </p>

        <p class="coach">{hint}</p>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll ribbon" role="group" aria-label="Rows to try">
          {Array.from({ length: MAX_ROWS }, (_, i) => i + 1).map((n) => (
            <Chip
              key={n}
              label={String(n)}
              pressed={rows === n}
              tinted={divisors.includes(n)}
              accent={divisors.includes(n) ? token('--crayon-green') : token('--ink-faint')}
              onClick={() => setRows(n)}
            />
          ))}
        </div>
        <div class="dock__row">
          <Stepper value={total} min={1} max={MAX_BLOCKS} label="blocks" onChange={setTotal} />
          <CrayonRow value={color} onChange={setColor} />
        </div>
      </div>
    </div>
  );
}

function drawBlock(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string, ghost: boolean): void {
  const r = Math.min(size * 0.28, 12);
  const half = size / 2;
  ctx.save();
  ctx.globalAlpha = ghost ? 0.85 : 1;
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
  ctx.lineWidth = Math.max(2, size * 0.07);
  ctx.beginPath();
  ctx.roundRect(x - half, y - half, size, size, r);
  ctx.fill();
  ctx.stroke();
  if (ghost) {
    // A leftover carries a little "not in the rectangle" tick mark.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = Math.max(2, size * 0.09);
    ctx.beginPath();
    ctx.moveTo(x - size * 0.16, y);
    ctx.lineTo(x + size * 0.16, y);
    ctx.stroke();
  }
  ctx.restore();
}
